import {ActionNames} from '@/constants/ActionNames';
import {IState} from '@/interfaces/IState';
import {ActionContext} from 'vuex';
import Api from '@/utils/api';
import API_ROUTES from '@/constants/ApiRoutes';
import {ILicense} from '@/interfaces/ILicense';
import {IInvoice} from '@/interfaces/IInvoice';
import {IArticle} from '@/interfaces/IArticle';
import {MutationNames} from '@/constants/MutationNames';
import {IOrder} from '@/interfaces/IOrder';

interface LoginData {
  email: string;
  password: string;
}

interface RegisterData {
  firstName: string;
  middleName: string;
  surName?: string;
  email: string;
  password: string;
  license: string;
}

interface UpdateData {
  firstName?: string;
  middleName?: string;
  surName?: string;
  email?: string;
  license?: string;
}

interface UpdateAddress {
  street: string;
  houseNumber: string;
  zipCode: string;
  city: string;
}

interface UpdatePasswordData {
  oldPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

export default {
  async [ActionNames.LOGIN](context: ActionContext<IState, any>, loginData: LoginData) {
    try {
      const json = await Api().post<{
        token: string,
        role: number,
      }>(API_ROUTES.ROUTE_LOGIN, loginData);

      if (json.status === 200) {
        context.commit(MutationNames.LOG_IN, {
          authToken: json.data.token,
          role: json.data.role,
        });
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.REGISTER](context: ActionContext<IState, any>, registerData: RegisterData) {
    try {
      const json = await Api().post(API_ROUTES.ROUTE_REGISTER, registerData);

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.GET_BALANCE](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{ balance: number }>(API_ROUTES.ROUTE_GET_BALANCE, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.balance = json.data.balance;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.UPDATE_BUSINESS_DATA](
      context: ActionContext<IState, any>, businessData: { vatNumber: string, businessName: string },
  ) {
    try {
      const json = await Api().post<{
        success: boolean,
      }>(API_ROUTES.ROUTE_UPDATE_BUSINESS_DATA, {
        businessName: businessData.businessName,
        vatNumber: businessData.vatNumber,
      }, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
          context.state.businessName = businessData.businessName;
          context.state.vatNumber = businessData.vatNumber;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.GET_USER_INFORMATION](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{
        firstName: string,
        prefix: string,
        lastName: string,
        email: string,
        relationId: number,
        cardNumber: number,
        street: string,
        houseNumber: string,
        zipCode: string,
        city: string,
        vatNumber: string,
        businessName: string,
      }>(API_ROUTES.ROUTE_GET_USER_INFORMATION, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.fullName = `${json.data.firstName} ${json.data.prefix} ${json.data.lastName}`;
        context.state.firstName = json.data.firstName;
        context.state.prefix = json.data.prefix;
        context.state.lastName = json.data.lastName;
        context.state.email = json.data.email;
        context.state.relationId = json.data.relationId;
        context.state.cardNumber = json.data.cardNumber;
        context.state.street = json.data.street;
        context.state.houseNumber = json.data.houseNumber;
        context.state.zipCode = json.data.zipCode;
        context.state.city = json.data.city;
        context.state.vatNumber = json.data.vatNumber;
        context.state.businessName = json.data.businessName;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.GET_PRIMARY_LICENSE](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{
        primaryLicense: string,
      }>(API_ROUTES.ROUTE_GET_USER_INFORMATION, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.primaryLicense = json.data.primaryLicense;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.GET_LICENSES](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{
        licenses: ILicense[],
      }>(API_ROUTES.ROUTE_GET_LICENSES, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.licenses = json.data.licenses;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.INVOICES](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{
        invoices: IInvoice[],
      }>(API_ROUTES.ROUTE_GET_INVOICES, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.invoices = json.data.invoices;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.DELETE_LICENSE](context: ActionContext<IState, any>, licenseData: ILicense) {
    try {
      const json = await Api().delete(`${API_ROUTES.ROUTE_DELETE_LICENSE}${licenseData.relationId}`, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        const licenses = context.state.licenses;

        licenses.map((license, index) => {
          if (license.relationId === licenseData.relationId) {
            licenses.splice(index, 1);
          }
        });

        context.state.licenses = licenses;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.ORDER_HISTORY](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{
        orders: IOrder[],
      }>(`${API_ROUTES.ROUTE_GET_ORDER_HISTORY}`, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.orders = json.data.orders;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.ADD_LICENSE](context: ActionContext<IState, any>, license: string) {
    try {
      const json = await Api().post<{
        relationId: number,
      }>(`${API_ROUTES.ROUTE_ADD_LICENSE}${license}`, {}, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        const licenses = context.state.licenses;

        licenses.push({
          relationId: json.data.relationId,
          license,
        });

        context.state.licenses = licenses;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.GET_ARTICLES](context: ActionContext<IState, any>) {
    try {
      const json = await Api().get<{
        articles: IArticle[],
      }>(`${API_ROUTES.ROUTE_GET_ARTICLES}${context.state.relationId}`);

      if (json.status === 200) {
        json.data.articles.sort((articleA: IArticle, articleB: IArticle) => {
          return articleA.amount - articleB.amount;
        });

        context.state.articles = json.data.articles;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.UPDATE_USER_DATA](context: ActionContext<IState, any>, updateData: UpdateData) {
    try {
      const data: UpdateData = {
        firstName: updateData.firstName ? updateData.firstName : context.state.firstName,
        middleName: updateData.middleName ? updateData.middleName : context.state.prefix,
        surName: updateData.surName ? updateData.surName : context.state.lastName,
        email: updateData.email ? updateData.email : context.state.email,
        license: updateData.license ? updateData.license : context.state.primaryLicense,
      };

      const json = await Api().put(API_ROUTES.ROUTE_UPDATE_USER_INFORMATION, data, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        context.state.firstName = updateData.firstName ? updateData.firstName : context.state.firstName;
        context.state.prefix = updateData.middleName ? updateData.middleName : context.state.prefix;
        context.state.lastName = updateData.surName ? updateData.surName : context.state.lastName;
        context.state.email = updateData.email ? updateData.email : context.state.email;
        context.state.primaryLicense = updateData.license ? updateData.license : context.state.primaryLicense;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.CREATE_ORDER](context: ActionContext<IState, any>, articleId: number) {
    try {
      const json = await Api().post<{
        url: string,
      }>(`${API_ROUTES.ROUTE_CREATE_ORDER}${articleId}`, {}, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        window.location.href = json.data.url;
      }

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.UPDATE_PASSWORD](context: ActionContext<IState, any>, updatePasswordData: UpdatePasswordData) {
    try {
      const json = await Api().post(`${API_ROUTES.ROUTE_CHANGE_PASSWORD}`, updatePasswordData, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.FORGOT_PASSWORD](context: ActionContext<IState, any>, email: string) {
    try {
      const json = await Api().get(`${API_ROUTES.ROUTE_RESET_PASSWORD}${email}`);

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.UPDATE_ADDRESS](context: ActionContext<IState, any>, updateAddressData: UpdateAddress) {
    try {
      const json = await Api().post(`${API_ROUTES.ROUTE_CHANGE_ADDRESS}`, updateAddressData, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      return json.status === 200;
    } catch (e) {
      return false;
    }
  },
  async [ActionNames.CREATE_INVOICE](context: ActionContext<IState, any>, invoiceId: number) {
    try {
      const json = await Api().post<{
        url: string,
      }>(`${API_ROUTES.ROUTE_CREATE_INVOICE}${invoiceId}`, {}, {
        headers: {
          'x-api-key': context.state.authToken,
        },
      });

      if (json.status === 200) {
        const jsonData = await Api().get(`${API_ROUTES.ROUTE_GET_INVOICE}${invoiceId}`, {
          headers: {
            'x-api-key': context.state.authToken,
            'Accept': 'application/pdf',
          },
          responseType: 'arraybuffer',
        });

        const blob = new Blob([jsonData.data], {
          type: 'application/pdf',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', `${invoiceId}.pdf`);
        document.body.appendChild(link);
        link.click();

        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  },
};
